<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.permits.documents.documents_dialog.title_new') }}
    </h1>
    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
      @submit.native.prevent="submitForm"
    >
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.name')"
          :error="errors?.name?.[0]"
          :class="form.name ? 'has-value' : ''"
          prop="name"
        >
          <el-input
            v-model="form.name"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :error="errors?.description?.[0]"
          :class="form.description ? 'has-value' : ''"
          prop="description"
          class="tw-flex-3"
        >
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="5"
            :placeholder="$t('common.fields.description')"
            :maxlength="512"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <el-form-item>
          <el-switch
            v-model="form.editable_by_client"
            :active-text="$t('common.fields.client_editable')"
            class="tw-mt-2"
          />
        </el-form-item>
        <el-form-item>
          <el-switch
            v-model="form.editable_by_foreigner"
            :active-text="$t('common.fields.foreigner_editable')"
            class="tw-mt-2"
          />
        </el-form-item>
      </div>
    </el-form>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { getValidationError } from '@/helpers';

const FORM_BASE = {
  name: '', // required, maxLength: 128
  description: '', // not required, but if filled - maxLength: 512
  editable_by_client: false,
  editable_by_foreigner: false,
};

export default {
  name: 'AddPermitDocumentDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      errors: {},
      form: _cloneDeep(FORM_BASE),
      formRules: {
        name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        description: [
          { max: 512, message: 'Max 512 characters', trigger: 'blur' },
        ],
        editable_by_client: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        editable_by_foreigner: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
      },
    };
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.form = _cloneDeep(FORM_BASE);
      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/permitManagement/documents/addDocument', {
          payload: this.form,
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
